import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/calendar";
import { AnalysisPowerChart } from "@/features/ui/PMS/chart/powerChart";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Text } from "@mantine/core";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";

export const AnalysisPowerPage = () => {
  const { openModal, closeModal } = useModal();
  const [dates, setDates] = useState<CalendarDate>([null, null]);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [itemCode, setItemCode] = useState<string>("");

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  const totalPowerColumns: readonly Column<any>[] = [
    {
      key: "totalPower",
      name: "총 소비 전력",
      sortable: true,
      resizable: true,
    },
  ];

  const powerColumns: readonly Column<any>[] = [
    {
      key: "power",
      name: "일 개당 평균 소비 전력",
      sortable: true,
      resizable: true,
    },
  ];
  const productionColumns: readonly Column<any>[] = [
    {
      key: "day",
      name: "일 생산 수량/카운트",
      sortable: true,
      resizable: true,
    },
  ];

  const machineColumns: readonly Column<any>[] = [
    {
      key: "make",
      name: "기계 제조사",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "기계 이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "제조 연월일",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "제조 번호",
      sortable: true,
      resizable: true,
    },
  ];

  const itemColumns: readonly Column<any>[] = [
    {
      key: "model",
      name: "모델",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "코드",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "품명",
      sortable: true,
      resizable: true,
    },

    {
      key: "spec",
      name: "규격",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <Flex gap="md" direction="column" p="md">
      <Flex justify="space-between">
        <Text size="xl">전력</Text>
        <Flex gap="md">
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(<Calendar dates={[null, null]} type="default" />, null, "날짜 선택").then(
                (selectedDates) => {
                  if (selectedDates) {
                    setDates(selectedDates);
                  }
                },
              );
            }}
          >
            {`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM-DD") : ""} 날짜선택`}
          </Button>
          <Button
            onClick={() => {
              openModal(<MachineSearchBox onSelect={handleSelectMachine} />, null, "기계 검색");
            }}
            leftIcon={<IconReportSearch />}
          >
            설비선택
          </Button>
        </Flex>
      </Flex>
      <DataGrid
        columns={machineColumns ?? []}
        rows={selectedMachine ? [selectedMachine] : []}
        rowHeight={40}
        rowKeyGetter={(row) => row.id}
        style={{ height: "120px", width: "100%" }}
      />
      <Flex gap="md">
        <DataGrid
          columns={itemColumns ?? []}
          rows={
            Array.isArray(machineItemMockupData[selectedMachine?.id ?? 0])
              ? machineItemMockupData[selectedMachine?.id ?? 0]
              : []
          }
          rowHeight={40}
          rowKeyGetter={(row) => row.code}
          onRowClick={(row) => {
            setItemCode(row.code);
          }}
          style={{ height: "100%", width: "30%" }}
        />
        <AnalysisPowerChart itemCode={itemCode} />
      </Flex>
      <Flex gap="md" w="100%">
        <DataGrid
          columns={productionColumns ?? []}
          rows={
            itemCode && Array.isArray(itemDetailMockupData[itemCode])
              ? itemDetailMockupData[itemCode]
              : []
          }
          rowHeight={40}
          style={{ height: "80px", width: "33%" }}
        />
        <DataGrid
          columns={powerColumns ?? []}
          rows={
            itemCode && Array.isArray(itemDetailMockupData[itemCode])
              ? itemDetailMockupData[itemCode]
              : []
          }
          rowHeight={40}
          style={{ height: "80px", width: "33%" }}
        />
        <DataGrid
          columns={totalPowerColumns ?? []}
          rows={
            itemCode && Array.isArray(itemDetailMockupData[itemCode])
              ? itemDetailMockupData[itemCode]
              : []
          }
          rowHeight={40}
          style={{ height: "80px", width: "33%" }}
        />
      </Flex>
    </Flex>
  );
};

export const machineItemMockupData: Record<
  string,
  Array<{
    model: string;
    code: string;
    name: string;
    spec: string;
  }>
> = {
  "1": [
    { model: "X100", code: "ITM001", name: "이젝터1", spec: "10x10mm" },
    { model: "X101", code: "ITM002", name: "이젝터2", spec: "12x12mm" },
    { model: "X102", code: "ITM003", name: "예비캠1", spec: "20x20mm" },
  ],
  "2": [
    { model: "X103", code: "ITM004", name: "예비캠2", spec: "14x18mm" },
    { model: "X104", code: "ITM005", name: "예비캠3", spec: "25x30mm" },
  ],
  "3": [
    { model: "X105", code: "ITM006", name: "프레스 커버", spec: "30x30mm" },
    { model: "X106", code: "ITM007", name: "고정 플레이트", spec: "40x40mm" },
  ],
  "4": [
    { model: "X107", code: "ITM008", name: "센서 플레이트", spec: "15x15mm" },
    { model: "X108", code: "ITM009", name: "로드 블록", spec: "60mm" },
    { model: "X109", code: "ITM010", name: "엔드 스토퍼", spec: "22x22mm" },
  ],
  "5": [
    { model: "X110", code: "ITM011", name: "모터 커버", spec: "50x50mm" },
    { model: "X111", code: "ITM012", name: "핀 고정부", spec: "28x28mm" },
  ],
  "6": [
    { model: "X112", code: "ITM013", name: "슬라이더", spec: "80x30mm" },
    { model: "X113", code: "ITM014", name: "센터 블럭", spec: "35x35mm" },
    { model: "X114", code: "ITM015", name: "고정 롤러", spec: "Ø40mm" },
  ],
  "7": [
    { model: "X115", code: "ITM016", name: "유압 실린더", spec: "Ø100mm" },
    { model: "X116", code: "ITM017", name: "스트로크 가이드", spec: "60x10mm" },
  ],
  "8": [
    { model: "X117", code: "ITM018", name: "프레임 조인트", spec: "70x30mm" },
    { model: "X118", code: "ITM019", name: "기어 플레이트", spec: "80x80mm" },
    { model: "X119", code: "ITM020", name: "베어링 커버", spec: "Ø65mm" },
  ],
  "9": [
    { model: "X120", code: "ITM021", name: "조정 너트", spec: "M12x1.5" },
    { model: "X121", code: "ITM022", name: "가이드 핀", spec: "Ø10x50mm" },
  ],
  "10": [
    { model: "X122", code: "ITM023", name: "베이스 플레이트", spec: "100x100mm" },
    { model: "X123", code: "ITM024", name: "체결 브라켓", spec: "45x20mm" },
    { model: "X124", code: "ITM025", name: "전선 고정대", spec: "30x15mm" },
  ],
  "11": [
    { model: "X125", code: "ITM026", name: "클러치", spec: "Ø80mm" },
    { model: "X126", code: "ITM027", name: "브레이크패드", spec: "220x50x10mm" },
  ],
  "12": [
    { model: "X127", code: "ITM028", name: "전원 모듈", spec: "300W" },
    { model: "X128", code: "ITM029", name: "온도 센서", spec: "PT100" },
  ],
  "13": [
    { model: "X129", code: "ITM030", name: "진동 댐퍼", spec: "120x60mm" },
    { model: "X130", code: "ITM031", name: "냉각핀", spec: "70x40mm" },
    { model: "X131", code: "ITM032", name: "커넥터 베이스", spec: "6핀" },
  ],
  "14": [
    { model: "X132", code: "ITM033", name: "릴레이 모듈", spec: "12V" },
    { model: "X133", code: "ITM034", name: "I/O 보드", spec: "16채널" },
  ],
  "15": [
    { model: "X134", code: "ITM035", name: "공압 밸브", spec: "5/2 방식" },
    { model: "X135", code: "ITM036", name: "에어 실린더", spec: "Ø25x75mm" },
  ],
  "16": [
    { model: "X136", code: "ITM037", name: "가스켓", spec: "Ø50mm" },
    { model: "X137", code: "ITM038", name: "커넥팅 로드", spec: "200mm" },
  ],
  "17": [
    { model: "X138", code: "ITM039", name: "윤활 펌프", spec: "자동식" },
    { model: "X139", code: "ITM040", name: "오일 필터", spec: "100μm" },
    { model: "X140", code: "ITM041", name: "탱크 브라켓", spec: "80x60mm" },
  ],
  "18": [
    { model: "X141", code: "ITM042", name: "제어기 커버", spec: "ABS" },
    { model: "X142", code: "ITM043", name: "디스플레이 모듈", spec: "7인치" },
  ],
  "19": [
    { model: "X143", code: "ITM044", name: "서보 모터", spec: "400W" },
    { model: "X144", code: "ITM045", name: "엔코더", spec: "360ppr" },
  ],
  "20": [
    { model: "X145", code: "ITM046", name: "파워서플라이", spec: "24V 5A" },
    { model: "X146", code: "ITM047", name: "단자대", spec: "16핀" },
    { model: "X147", code: "ITM048", name: "안전 커버", spec: "투명 PC" },
  ],
};

const itemDetailMockupData: Record<
  string,
  Array<{ day: string; power: string; totalPower: string }>
> = {
  ITM001: [{ day: "10 ea", power: "54 kW", totalPower: "1436 kWh" }],
  ITM002: [{ day: "24 ea", power: "89 kW", totalPower: "1212 kWh" }],
  ITM003: [{ day: "31 ea", power: "129 kW", totalPower: "973 kWh" }],
  ITM004: [{ day: "24 ea", power: "78 kW", totalPower: "1167 kWh" }],
  ITM005: [{ day: "55 ea", power: "131 kW", totalPower: "984 kWh" }],
  ITM006: [{ day: "47 ea", power: "66 kW", totalPower: "1499 kWh" }],
  ITM007: [{ day: "57 ea", power: "148 kW", totalPower: "538 kWh" }],
  ITM008: [{ day: "48 ea", power: "124 kW", totalPower: "1490 kWh" }],
  ITM009: [{ day: "91 ea", power: "63 kW", totalPower: "1026 kWh" }],
  ITM010: [{ day: "101 ea", power: "72 kW", totalPower: "1350 kWh" }],
  ITM011: [{ day: "211 ea", power: "125 kW", totalPower: "697 kWh" }],
  ITM012: [{ day: "142 ea", power: "97 kW", totalPower: "812 kWh" }],
  ITM013: [{ day: "136 ea", power: "144 kW", totalPower: "690 kWh" }],
  ITM014: [{ day: "144 ea", power: "143 kW", totalPower: "1340 kWh" }],
  ITM015: [{ day: "145 ea", power: "78 kW", totalPower: "505 kWh" }],
  ITM016: [{ day: "176 ea", power: "148 kW", totalPower: "533 kWh" }],
  ITM017: [{ day: "167 ea", power: "129 kW", totalPower: "1479 kWh" }],
  ITM018: [{ day: "188 ea", power: "101 kW", totalPower: "1480 kWh" }],
  ITM019: [{ day: "192 ea", power: "98 kW", totalPower: "934 kWh" }],
  ITM020: [{ day: "200 ea", power: "132 kW", totalPower: "622 kWh" }],
};
